<script lang="ts">
import { reactive, computed } from 'vue';
import { remoteFileToDataSource } from '@/src/io/import/dataSource';
import { loadDataSources } from '@/src/actions/loadUserFiles';
import useVolumeColoringStore from '@/src/store/view-configs/volume-coloring';
import { SAMPLE_DATA } from '@/src/config';
import { useMessageStore } from '@/src/store/messages';
import { SampleDataset } from '@/src/types';
import { useImageStore } from '@/src/store/datasets-images';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import { fetchFile } from '@/src/utils/fetch';
import { useViewStore } from '@/src/store/views';

enum ProgressState {
  Pending,
  Error,
  Done,
}

interface Progress {
  [name: string]: {
    state: ProgressState;
    progress: number;
  };
}

export default {
  setup() {
    const viewStore = useViewStore();
    const status = reactive<{ progress: Progress }>({
      progress: {},
    });
    // URL -> data ID
    const loaded = reactive<{
      urlToID: Record<string, string>;
      idToURL: Record<string, string>;
    }>({
      urlToID: {},
      idToURL: {},
    });

    const clearLoadedStatus = (id: string) => {
      const url = loaded.idToURL[id];
      if (url) {
        delete loaded.idToURL[id];
        delete loaded.urlToID[url];
      }
    };

    const imageStore = useImageStore();
    imageStore.$onAction(({ name, args, after }) => {
      if (name === 'deleteData') {
        after(() => {
          const [id] = args;
          clearLoadedStatus(id as string);
        });
      }
    });

    const dicomStore = useDICOMStore();
    dicomStore.$onAction(({ name, args, after }) => {
      if (name === 'deleteVolume') {
        after(() => {
          const [key] = args;
          clearLoadedStatus(key as string);
        });
      }
    });

    async function downloadSample(sample: SampleDataset) {
      const progress = (percent: number) => {
        status.progress[sample.name] = {
          state: ProgressState.Pending,
          progress: percent * 100,
        };
      };
      try {
        progress(Infinity);

        const sampleFile = await fetchFile(sample.url, sample.filename, {
          progress,
        });
        status.progress[sample.name].state = ProgressState.Done;

        await loadDataSources([remoteFileToDataSource(sampleFile, sample.url)]);

        const selection = viewStore
          .getAllViews()
          .map((view) => view.dataID)
          .find(Boolean);
        if (!selection) {
          throw new Error('Failed to load sample data');
        }

        loaded.idToURL[selection] = sample.url;
        loaded.urlToID[sample.url] = selection;

        useVolumeColoringStore().setDefaults(selection, {
          transferFunction: {
            preset: sample.defaults?.colorPreset,
          },
        });
      } catch (error) {
        status.progress[sample.name].state = ProgressState.Error;
        const messageStore = useMessageStore();
        messageStore.addError('Failed to load sample data', {
          error: error as Error,
        });
      } finally {
        delete status.progress[sample.name];
      }
    }

    const samples = computed(() =>
      SAMPLE_DATA.map((sample) => {
        const isDone =
          status.progress[sample.name]?.state === ProgressState.Done;
        const isError =
          status.progress[sample.name]?.state === ProgressState.Error;
        const isLoaded = !!loaded.urlToID[sample.url];
        const progress =
          isDone || isLoaded
            ? 100
            : Math.floor(status.progress[sample.name]?.progress);
        return {
          ...sample,
          isDownloading: sample.name in status.progress,
          isDone,
          isError,
          isLoaded,
          indeterminate: progress === Infinity,
          progress,
        };
      })
    );

    return {
      samples,
      downloadSample,
    };
  },
};
</script>

<template>
  <div data-testid="samples-list" class="pycad-samples">
    <button
      v-for="sample in samples"
      :key="sample.name"
      type="button"
      class="pycad-sample-card"
      :class="{
        'pycad-sample-card--busy': sample.isDownloading,
        'pycad-sample-card--loaded': sample.isLoaded,
        'pycad-sample-card--error': sample.isError,
      }"
      :disabled="sample.isDownloading || sample.isLoaded"
      :title="sample.name"
      @click="downloadSample(sample)"
    >
      <div class="pycad-sample-card__thumb">
        <img :src="sample.image" :alt="sample.name" />
        <div
          v-if="sample.isDownloading || sample.isLoaded"
          class="pycad-sample-card__thumb-overlay"
        >
          <i
            v-if="sample.isDone || sample.isLoaded"
            class="mdi mdi-check-bold"
          ></i>
          <i v-else-if="sample.isError" class="mdi mdi-alert-circle"></i>
          <i
            v-else-if="sample.indeterminate"
            class="mdi mdi-loading pycad-sample-card__spinner"
          ></i>
          <span v-else class="pycad-sample-card__pct">
            {{ sample.progress }}%
          </span>
        </div>
      </div>

      <div class="pycad-sample-card__body">
        <div class="pycad-sample-card__title-row">
          <span class="pycad-sample-card__title">{{ sample.name }}</span>
          <span v-if="sample.isLoaded" class="pycad-sample-card__badge">
            Loaded
          </span>
        </div>
        <p class="pycad-sample-card__desc">{{ sample.description }}</p>
        <div class="pycad-sample-card__cta">
          <template v-if="sample.isDownloading">
            <i class="mdi mdi-progress-download"></i>
            <span>Downloading…</span>
          </template>
          <template v-else-if="sample.isLoaded">
            <i class="mdi mdi-eye-outline"></i>
            <span>Open in viewer</span>
          </template>
          <template v-else>
            <i class="mdi mdi-download"></i>
            <span>Load case</span>
          </template>
        </div>
      </div>

      <div v-if="sample.isDownloading" class="pycad-sample-card__progress">
        <div
          class="pycad-sample-card__progress-fill"
          :class="{
            'pycad-sample-card__progress-fill--indeterminate':
              sample.indeterminate,
          }"
          :style="
            sample.indeterminate ? undefined : { width: sample.progress + '%' }
          "
        ></div>
      </div>
    </button>
  </div>
</template>

<style scoped>
.pycad-samples {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: 'Inter', system-ui, sans-serif;
}

.pycad-sample-card {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 12px;
  padding: 10px;
  text-align: left;
  background: var(--pycad-elevated, #161616);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius, 10px);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  font-family: inherit;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;
}

.pycad-sample-card:hover:not(:disabled),
.pycad-sample-card:focus-visible {
  border-color: var(--pycad-primary-border, rgba(255, 153, 0, 0.45));
  box-shadow: var(--pycad-shadow-glow, 0 0 10px rgba(255, 153, 0, 0.22));
}

.pycad-sample-card:disabled {
  cursor: default;
}

.pycad-sample-card--loaded {
  border-color: var(--pycad-primary-border, rgba(255, 153, 0, 0.45));
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.14));
}

.pycad-sample-card--error {
  border-color: rgba(255, 92, 92, 0.55);
}

.pycad-sample-card__thumb {
  position: relative;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  border-radius: var(--pycad-radius-sm, 6px);
  overflow: hidden;
  background: var(--pycad-surface, #0b0b0b);
  border: 1px solid var(--pycad-border, #282828);
}

.pycad-sample-card__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.pycad-sample-card__thumb-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.65);
  color: var(--pycad-primary, #ff9900);
  font-size: 24px;
}

.pycad-sample-card__pct {
  font-size: 12px;
  font-weight: 700;
  color: var(--pycad-primary, #ff9900);
  font-variant-numeric: tabular-nums;
}

.pycad-sample-card__spinner {
  animation: pycad-spin 0.9s linear infinite;
}

.pycad-sample-card__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.pycad-sample-card__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.pycad-sample-card__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--pycad-text-primary, #fff);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pycad-sample-card__badge {
  flex-shrink: 0;
  padding: 1px 7px;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--pycad-primary, #ff9900);
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.14));
  border: 1px solid var(--pycad-primary-border, rgba(255, 153, 0, 0.45));
}

.pycad-sample-card__desc {
  margin: 0;
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--pycad-text-secondary, #a0a0a0);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pycad-sample-card__cta {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: auto;
  padding-top: 2px;
  font-size: 11px;
  font-weight: 600;
  color: var(--pycad-text-muted, #666);
  transition: color 0.15s ease;
}

.pycad-sample-card:hover:not(:disabled) .pycad-sample-card__cta,
.pycad-sample-card--busy .pycad-sample-card__cta,
.pycad-sample-card--loaded .pycad-sample-card__cta {
  color: var(--pycad-primary, #ff9900);
}

.pycad-sample-card__cta .mdi {
  font-size: 14px;
}

.pycad-sample-card__progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: var(--pycad-border, #282828);
}

.pycad-sample-card__progress-fill {
  height: 100%;
  background: var(--pycad-primary, #ff9900);
  transition: width 0.2s ease;
}

.pycad-sample-card__progress-fill--indeterminate {
  width: 35%;
  animation: pycad-sample-indeterminate 1.2s ease-in-out infinite;
}

@keyframes pycad-sample-indeterminate {
  0% {
    margin-left: -35%;
  }
  100% {
    margin-left: 100%;
  }
}
</style>
